import {useEffect, useRef} from 'react';
import {fetchEventSource} from '@microsoft/fetch-event-source';
import axios from 'axios';

class NonRetryableError extends Error {}

function buildHeaders(): Record<string, string> {
  const common = axios.defaults.headers.common as Record<string, string>;
  const headers: Record<string, string> = {
    Authorization: common.Authorization ?? '',
    API_KEY: common.API_KEY ?? (process.env.REACT_APP_API_KEY ?? '')
  };

  if (common.CLEVER_TOKEN) {
    headers.CLEVER_TOKEN = common.CLEVER_TOKEN;
  }

  return headers;
}

const FORCE_BACK_STATUSES = new Set(['paused', 'withdrawn', 'screen_failed', 'completed']);

export const useStepContentSse = (apId: number, stepId: number, onForceReturn: (reason: string) => void): void => {
  const onForceReturnRef = useRef(onForceReturn);

  useEffect(() => {
    onForceReturnRef.current = onForceReturn;
  });

  useEffect(() => {
    const ctrl = new AbortController();
    const url = `${process.env.REACT_APP_API_URL ?? ''}/patient/stream/run/${apId}`;

    fetchEventSource(url, {
      headers: buildHeaders(),
      signal: ctrl.signal,

      onopen: async (response) => {
        if (response.status === 401 || response.status === 404) {
          throw new NonRetryableError(`SSE ${response.status} for ap ${apId}`);
        }
      },

      onmessage: (msg) => {
        const code = msg.event;

        if (!code) {
          return;
        }

        switch (code) {
          case 'assignment_status_changed': {
            try {
              const data = JSON.parse(msg.data);

              if (FORCE_BACK_STATUSES.has(data?.status)) {
                onForceReturnRef.current('status_changed');
              }
            } catch {
              // event data may be absent or malformed
            }
            break;
          }
          case 'op_rule_action_executed': {
            try {
              const data = JSON.parse(msg.data);

              if (data?.action === 'pause') {
                onForceReturnRef.current('paused');
              } else if (data?.action === 'skip_step' && data?.stepId === stepId) {
                onForceReturnRef.current('step_skipped');
              }
            } catch {
              // event data may be absent or malformed
            }
            break;
          }
          default:
            break;
        }
      },

      onerror: (err) => {
        if (err instanceof NonRetryableError) {
          throw err;
        }
        console.error(`SSE error for step ${stepId} of program ${apId}:`, err);
      }
    }).catch((err) => {
      if (!(err instanceof NonRetryableError)) {
        console.error(`SSE stream failed for step ${stepId} of program ${apId}:`, err);
      }
    });

    return () => {
      ctrl.abort();
    };
  }, [apId, stepId]);
};
